import { Editor, useEditorState } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '../../../Select';

import classes from './List.module.scss';

enum ListType {
  task = 'TASK',
  ordered = 'ORDERED',
  unordered = 'UNORDERED',
}

interface ListProps {
  editor: Editor;
}

export const List: FunctionComponent<ListProps> = ({ editor }) => {
  const { t } = useTranslation();

  const { isActiveTask, isActiveOrdered, isActiveBullet } = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isActiveTask: editor.isActive('taskList'),
      isActiveOrdered: editor.isActive('orderedList'),
      isActiveBullet: editor.isActive('bulletList'),
    }),
  });

  const items = useMemo(
    () => [
      { id: ListType.task, title: t('richTextEditor.tool-names-check-list') },
      { id: ListType.ordered, title: t('richTextEditor.tools-list-ordered') },
      { id: ListType.unordered, title: t('richTextEditor.tools-list-unordered') },
    ],
    [t]
  );

  const selectedItemId = isActiveTask
    ? ListType.task
    : isActiveOrdered
    ? ListType.ordered
    : isActiveBullet
    ? ListType.unordered
    : undefined;

  const onChange = useCallback(
    (type: ListType) => {
      switch (type) {
        case ListType.task:
          return editor.chain().focus().toggleTaskList().run();
        case ListType.ordered:
          return editor.chain().focus().toggleOrderedList().run();
        case ListType.unordered:
          return editor.chain().focus().toggleBulletList().run();
      }
    },

    [editor]
  );

  const allowed = useMemo(
    () =>
      ['paragraph', 'heading', 'taskList', 'orderedList', 'bulletList'].includes(
        editor.state.selection.$from.node(1)?.type.name
      ),
    [editor.state.selection]
  );

  return (
    <Select
      className={classes['list']}
      placeholderLabel={t('richTextEditor.tool-names-list')}
      selectedItemId={selectedItemId}
      active={Boolean(selectedItemId)}
      items={items}
      onChange={(type) => onChange(type as ListType)}
      disabled={!allowed}
      noBorder
    />
  );
};
